/**
 * EntityDetailRedirect - Handles deep links to entity detail pages
 * 
 * When a user navigates to /vehicles/:id, /customers/:id, etc.,
 * this component redirects to the list page with a query param
 * that tells the list page to open the entity dialog.
 * 
 * This preserves the existing dialog-based UX while supporting deep links.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface EntityDetailRedirectProps {
  /** The list route to redirect to (e.g., '/vehicles') */
  listRoute: string;
  /** Query param name to use for the entity ID */
  queryParam?: string;
}

/**
 * Redirects to the list page with the entity ID as a query parameter
 * The list page will then open the entity dialog automatically
 */
export function EntityDetailRedirect({ 
  listRoute, 
  queryParam = 'open' 
}: EntityDetailRedirectProps) {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      // Redirect to list page with query param to open the entity
      navigate(`${listRoute}?${queryParam}=${id}`, { replace: true });
    } else {
      // No ID provided, just go to list
      navigate(listRoute, { replace: true });
    }
  }, [id, listRoute, queryParam, navigate]);

  // Show loading while redirecting
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-sm text-muted-foreground">{t('loading')}</p>
      </div>
    </div>
  );
}
